import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faXmark, faExpand } from "@fortawesome/free-solid-svg-icons";
import { faAndroid, faApple } from "@fortawesome/free-brands-svg-icons";
import { gsap } from "gsap";
import "./styles/project.css";

const Lightbox = ({ images, onClose }) => {
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		const onKey = (e) => { if (e.key === "Escape") onClose(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<div className="lightbox-overlay" onClick={onClose}>
			<button className="lightbox-close" onClick={onClose}>
				<FontAwesomeIcon icon={faXmark} />
			</button>
			<div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
				<img src={images[current]} alt="preview" className="lightbox-img" />
				{images.length > 1 && (
					<div className="lightbox-thumbs">
						{images.map((img, i) => (
							<img
								key={i}
								src={img}
								alt={`thumb-${i}`}
								className={`lightbox-thumb ${current === i ? "lightbox-thumb-active" : ""}`}
								onClick={() => setCurrent(i)}
							/>
						))}
					</div>
				)}
			</div>
		</div>,
		document.body
	);
};

const Project = ({ logo, title, description, linkText, link, linkAndroid, linkIOS, image, imageAndroid, tags, category }) => {
	const [expanded, setExpanded] = useState(false);
	const [lightbox, setLightbox] = useState(false);
	const cardRef = useRef(null);
	const logos = Array.isArray(logo) ? logo : [logo];
	const isLong = description.length > 160;
	const images = [image, imageAndroid].filter(Boolean);

	useEffect(() => {
		const card = cardRef.current;
		if (!card) return;

		const onMove = (e) => {
			const { left, top, width, height } = card.getBoundingClientRect();
			const x = (e.clientX - left) / width - 0.5;
			const y = (e.clientY - top) / height - 0.5;
			gsap.to(card, {
				rotateY: x * 12,
				rotateX: -y * 12,
				scale: 1.03,
				duration: 0.4,
				ease: 'power2.out',
				transformPerspective: 800,
			});
		};

		const onLeave = () => {
			gsap.to(card, { rotateY: 0, rotateX: 0, scale: 1, duration: 0.5, ease: 'power2.out' });
		};

		card.addEventListener('mousemove', onMove);
		card.addEventListener('mouseleave', onLeave);
		return () => {
			card.removeEventListener('mousemove', onMove);
			card.removeEventListener('mouseleave', onLeave);
		};
	}, []);

	return (
		<>
			<div className="project" ref={cardRef}>

				{/* image */}
				<div className="project-image-wrap" onClick={() => setLightbox(true)}>
					<img src={image} alt={title} className="project-image" />
					{imageAndroid && (
						<img src={imageAndroid} alt={title + " android"} className="project-image project-image-secondary" />
					)}
					<span className="project-category-tag">{category}</span>
					<div className="project-image-zoom-hint">
						<FontAwesomeIcon icon={faExpand} /> View
					</div>
				</div>

				{/* body */}
				<div className="project-body">

					<div className="project-logos">
						{logos.map((l, i) => (
							<img key={i} src={l} alt="tech" className="project-logo" />
						))}
					</div>

					<div className="project-title">{title}</div>

					<div className={`project-description ${expanded ? "project-description-expanded" : ""}`}>
						{description}
					</div>
					{isLong && (
						<button className="project-read-more" onClick={() => setExpanded(!expanded)}>
							{expanded ? "Show less ↑" : "Read more ↓"}
						</button>
					)}

					{tags && tags.length > 0 && (
						<div className="project-tags">
							{tags.map((tag, i) => (
								<span key={i} className="project-tag">{tag}</span>
							))}
						</div>
					)}

					<div className="project-links">
						<a href={link} target="_blank" rel="noreferrer" className="project-link-btn project-link-primary">
							<FontAwesomeIcon icon={faArrowUpRightFromSquare} /> {linkText}
						</a>
						{linkIOS && (
							<a href={linkIOS} target="_blank" rel="noreferrer" className="project-link-btn project-link-ios">
								<FontAwesomeIcon icon={faApple} /> App Store
							</a>
						)}
						{linkAndroid && (
							<a href={linkAndroid} target="_blank" rel="noreferrer" className="project-link-btn project-link-android">
								<FontAwesomeIcon icon={faAndroid} /> Google Play
							</a>
						)}
					</div>

				</div>
			</div>

			{lightbox && <Lightbox images={images} onClose={() => setLightbox(false)} />}
		</>
	);
};

export default Project;
